import { useState, useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  Send,
  MapPin,
  Mail,
  Phone,
  CheckCircle,
  Clock,
  Globe,
  MessageCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const serviceOptions = [
  "Web Development",
  "Mobile App Development",
  "Custom Software",
  "Cloud Solutions",
  "Cybersecurity",
  "IT Consulting",
  "Other",
];
const budgetOptions = [
  "$5K - $15K",
  "$15K - $50K",
  "$50K - $100K",
  "$100K+",
  "Not Sure",
];

export default function ContactPage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="contact-page"
    >
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[50vh] flex items-center overflow-hidden pt-20"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute top-1/3 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-[150px]"
          />
        </motion.div>
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 py-20"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block"
          >
            Contact Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6"
          >
            Let's <span className="glow-text">Talk</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed"
          >
            Ready to start your next project? We'd love to hear from you. Fill
            out the form below and we'll get back within 4 hours.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Info + Form */}
      <section className="relative py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            <ContactInfo />
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Map/CTA Section */}
      <section className="relative py-24 bg-[#0a0f1e]">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FAQSection />
        </div>
      </section>
    </motion.div>
  );
}

function ContactInfo() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const contacts = [
    {
      icon: Mail,
      title: "Email",
      detail: "info@softlume.in",
      sub: "We reply within 4 hours",
    },
    {
      icon: Phone,
      title: "Phone",
      detail: "+91 7827782844",
      sub: "Mon-Sat, 10am-7pm IST",
    },
    {
      icon: MapPin,
      title: "Office",
      detail: "Ghitorni",
      sub: "New Delhi 110030",
    },
    {
      icon: Clock,
      title: "Business Hours",
      detail: "Monday - Saturday",
      sub: "10:00 AM - 7:00 PM IST",
    },
    {
      icon: Globe,
      title: "Global Reach",
      detail: "Clients in 15+ countries",
      sub: "Remote-first team",
    },
    {
      icon: MessageCircle,
      title: "Response Time",
      detail: "Under 4 hours",
      sub: "For all inquiries",
    },
  ];
  return (
    <div ref={ref} className="lg:col-span-2">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-heading font-bold text-3xl text-white mb-8">
          Contact Information
        </h2>
        <div className="space-y-5">
          {contacts.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="flex items-start gap-4 group"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                <item.icon className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-white">
                  {item.title}
                </div>
                <div className="text-sm text-slate-300">{item.detail}</div>
                <div className="text-xs text-slate-500 mt-0.5">{item.sub}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function ContactForm() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);

      // Track Google Ads conversion
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-18139644323/eCLkCLzuraccEKOD1MlD",
          value: 1.0,
          currency: "INR",
        });
      }

      setSubmitted(true);
      toast.success("Message sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="lg:col-span-3"
    >
      <form
        onSubmit={handleSubmit}
        data-testid="contact-page-form"
        className="p-6 md:p-8 rounded-2xl border border-white/5 bg-slate-900/30 backdrop-blur-md hover:border-white/10 transition-colors duration-500"
      >
        <h3 className="font-heading font-semibold text-xl text-white mb-6">
          Send Us a Message
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <Label htmlFor="cp-name" className="text-slate-300 text-sm">
              Full Name *
            </Label>
            <Input
              id="cp-name"
              name="name"
              data-testid="contact-page-name"
              value={form.name}
              onChange={handleChange}
              placeholder="John Doe"
              className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-email" className="text-slate-300 text-sm">
              Email Address *
            </Label>
            <Input
              id="cp-email"
              name="email"
              type="email"
              data-testid="contact-page-email"
              value={form.email}
              onChange={handleChange}
              placeholder="john@company.com"
              className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 h-11"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <Label htmlFor="cp-company" className="text-slate-300 text-sm">
              Company
            </Label>
            <Input
              id="cp-company"
              name="company"
              data-testid="contact-page-company"
              value={form.company}
              onChange={handleChange}
              placeholder="Acme Inc."
              className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-service" className="text-slate-300 text-sm">
              Service
            </Label>
            <select
              id="cp-service"
              name="service"
              data-testid="contact-page-service"
              value={form.service}
              onChange={handleChange}
              className="flex h-11 w-full rounded-md border border-white/10 bg-slate-800/50 px-3 py-2 text-sm text-white focus:border-blue-500/50 focus:outline-none"
            >
              <option value="" className="bg-slate-900 text-slate-400">
                Select a service
              </option>
              {serviceOptions.map((s) => (
                <option key={s} value={s} className="bg-slate-900 text-white">
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="space-y-2 mb-6">
          <Label htmlFor="cp-message" className="text-slate-300 text-sm">
            Project Details *
          </Label>
          <Textarea
            id="cp-message"
            name="message"
            data-testid="contact-page-message"
            value={form.message}
            onChange={handleChange}
            placeholder="Tell us about your project, goals, and timeline..."
            rows={6}
            className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 resize-none"
          />
        </div>
        <Button
          type="submit"
          data-testid="contact-page-submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl h-12 text-base font-semibold hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Sending...
            </span>
          ) : submitted ? (
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Sent Successfully!
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Message
            </span>
          )}
        </Button>
      </form>
    </motion.div>
  );
}

function FAQSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const faqs = [
    {
      q: "How long does a typical project take?",
      a: "Project timelines vary based on complexity. A simple web application might take 4-8 weeks, while enterprise solutions can span 3-6 months. We provide detailed timelines during our initial consultation.",
    },
    {
      q: "What is your development process?",
      a: "We follow an Agile methodology with 2-week sprints, regular demos, and continuous feedback loops. This ensures transparency and allows for adjustments throughout the project.",
    },
    {
      q: "Do you provide ongoing support?",
      a: "Yes! We offer comprehensive maintenance and support packages including bug fixes, feature updates, performance monitoring, and 24/7 emergency support for critical systems.",
    },
    {
      q: "What technologies do you work with?",
      a: "We work with a wide range of modern technologies including React, Next.js, Node.js, Python, React Native, Flutter, AWS, GCP, and many more. We choose the best stack for each project.",
    },
  ];
  return (
    <div ref={ref}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        className="text-center mb-12"
      >
        <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
          FAQ
        </span>
        <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-white">
          Frequently Asked Questions
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {faqs.map((faq, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 + i * 0.1 }}
            className="p-6 rounded-2xl border border-white/5 bg-slate-900/30 hover:border-blue-500/20 transition-all duration-300"
          >
            <h4 className="font-heading font-semibold text-base text-white mb-2">
              {faq.q}
            </h4>
            <p className="text-sm text-slate-400 leading-relaxed">{faq.a}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
